import { ModalWrapper } from '@/shared';
import React, { useEffect, useState } from 'react';
import { useDeleteResident } from '../../hooks';

interface DeleteResidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  resident: { id: string; name: string };
}

export const DeleteResidentModal: React.FC<DeleteResidentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  resident,
}) => {
  const [showError, setShowError] = useState(false);

  const { mutate: deleteResident, isPending } = useDeleteResident();

  const handleConfirmDelete = () => {
    deleteResident(resident.id, {
      onSuccess: () => {
        onConfirm();
        onClose(); // ✅ Close only after delete success
      },
    });
  };

  const handleCancleDelete = () => {
    setShowError(false);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setShowError(false);
    }
  }, [isOpen, resident.id]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center w-full min-h-full">
        <div className="w-xl p-8 bg-white rounded-4xl">
          <svg
            width="61"
            height="64"
            viewBox="0 0 61 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M25.1967 0C21.3823 0 17.7241 1.51528 15.0269 4.21249C12.3296 6.90971 10.8144 10.5679 10.8144 14.3823C10.8144 18.1968 12.3296 21.855 15.0269 24.5522C17.7241 27.2494 21.3823 28.7647 25.1967 28.7647C29.0111 28.7647 32.6693 27.2494 35.3666 24.5522C38.0638 21.855 39.5791 18.1968 39.5791 14.3823C39.5791 10.5679 38.0638 6.90971 35.3666 4.21249C32.6693 1.51528 29.0111 0 25.1967 0ZM26.6431 35.7663C27.4495 34.5951 26.6815 32.8159 25.2575 32.8191H23.9455L21.4015 32.8223C15.7255 32.8223 10.2819 35.0771 6.26836 39.0906C2.2548 43.1042 0 48.5478 0 54.2238C0 56.5407 0.920394 58.7628 2.55871 60.4011C4.19702 62.0394 6.41905 62.9598 8.73597 62.9598H25.4431C26.8831 62.9598 27.6607 61.123 26.8255 59.9486C24.2792 56.3818 22.9139 52.1071 22.9215 47.7246C22.9215 43.283 24.2975 39.1647 26.6431 35.7663Z"
              fill="#E03A3D"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M60.2646 47.728C60.2646 49.8649 59.8437 51.9808 59.026 53.955C58.2082 55.9292 57.0096 57.723 55.4986 59.234C53.9876 60.745 52.1938 61.9436 50.2196 62.7613C48.2454 63.5791 46.1295 63.9999 43.9926 63.9999C41.8558 63.9999 39.7398 63.5791 37.7656 62.7613C35.7914 61.9436 33.9976 60.745 32.4866 59.234C30.9757 57.723 29.7771 55.9292 28.9593 53.955C28.1416 51.9808 27.7207 49.8649 27.7207 47.728C27.7207 43.4124 29.4351 39.2736 32.4866 36.222C35.5382 33.1704 39.6771 31.4561 43.9926 31.4561C48.3082 31.4561 52.4471 33.1704 55.4986 36.222C58.5502 39.2736 60.2646 43.4124 60.2646 47.728ZM51.5414 40.176C51.7649 40.399 51.9422 40.6638 52.0632 40.9554C52.1842 41.247 52.2465 41.5595 52.2465 41.8752C52.2465 42.1909 52.1842 42.5035 52.0632 42.7951C51.9422 43.0866 51.7649 43.3515 51.5414 43.5744L48.5174 46.5952C48.3684 46.7438 48.2502 46.9204 48.1695 47.1148C48.0889 47.3092 48.0474 47.5175 48.0474 47.728C48.0474 47.9385 48.0889 48.1468 48.1695 48.3412C48.2502 48.5356 48.3684 48.7122 48.5174 48.8608L51.5414 51.8848C51.9653 52.3397 52.1961 52.9415 52.1852 53.5632C52.1742 54.185 51.9223 54.7782 51.4826 55.218C51.0429 55.6577 50.4497 55.9096 49.8279 55.9205C49.2061 55.9315 48.6044 55.7007 48.1494 55.2768L45.1254 52.2528C44.8254 51.9528 44.4185 51.7843 43.9942 51.7843C43.57 51.7843 43.1631 51.9528 42.863 52.2528L39.8391 55.2768C39.3841 55.7007 38.7824 55.9315 38.1606 55.9205C37.5388 55.9096 36.9456 55.6577 36.5059 55.218C36.0662 54.7782 35.8143 54.185 35.8033 53.5632C35.7923 52.9415 36.0231 52.3397 36.4471 51.8848L39.4679 48.8608C39.7678 48.5607 39.9363 48.1539 39.9363 47.7296C39.9363 47.3053 39.7678 46.8984 39.4679 46.5984L36.4439 43.5744C36.0065 43.122 35.7643 42.5158 35.7694 41.8866C35.7746 41.2573 36.0267 40.6552 36.4715 40.21C36.9163 39.7648 37.5181 39.5122 38.1474 39.5064C38.7766 39.5006 39.383 39.7423 39.8359 40.1792L42.863 43.2032C43.1631 43.5032 43.57 43.6717 43.9942 43.6717C44.4185 43.6717 44.8254 43.5032 45.1254 43.2032L48.1494 40.1792C48.5994 39.7298 49.2094 39.4773 49.8454 39.4773C50.4814 39.4773 51.0914 39.7266 51.5414 40.176Z"
              fill="#E03A3D"
            />
          </svg>

          <div className="my-4 md:my-8">
            <h4 className="text-xl md:text-3xl font-bold text-text-primary mb-2 ">
              Remove resident ({resident.name})
            </h4>
            <p className="text-base md:text-xl text-text-secondary ">
              Are you sure you want to permanently remove this resident? All
              their records will be deleted and can’t be recovered.
            </p>
          </div>

          {/* Error message */}
          {showError && (
            <p className="text-sm text-red-500 mb-4">
              Please confirm before deleting this house.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <button
              className="p-2 text-[#5761EB] text-base md:text-lg font-bold cursor-pointer rounded-md border border-[#ffffff] hover:border-[#5761EB] transition-colors duration-200"
              onClick={handleCancleDelete}
            >
              Cancle
            </button>
            <button
              className="p-2 text-base md:text-lg text-[#891518] font-bold rounded-md border border-[#ffffff] hover:border-[#891518]  transition-colors  duration-200"
              onClick={handleConfirmDelete}
            >
              {isPending ? 'Deleting' : 'Remove resident'}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
